import * as React from 'react';
import { Drawer } from 'vaul';
import { Slot } from '@radix-ui/react-slot';
import { X } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  bottomSheetBackdropRecipe,
  bottomSheetBodyRecipe,
  bottomSheetFooterRecipe,
  bottomSheetHeaderRecipe,
  bottomSheetRecipe,
  type BottomSheetRecipeProps,
} from '../recipes/sheet.recipe';
import { Text } from './Text';
import { Button } from './Button';

/*
 * Whether this sheet is open inside another. A sheet opened from inside a
 * sheet has to be vaul's NestedRoot, which is what pushes the one underneath
 * back as the new one rises — "panel stacking" in the Figma. Worked out here
 * rather than asked of the caller, because a caller who forgets gets two
 * sheets drawn flat on top of each other with nothing saying which is live.
 */
const NestedContext = React.createContext(false);

/*
 * The enclosing sheet: whether it can be put away, and how to close it.
 *
 * `close` exists because vaul, told `dismissible={false}`, refuses every
 * close — its own Close button included — so the onboarding sheet's
 * Continue could not shut it. The open state lives here instead, and
 * BottomSheetClose closes through it, which works either way.
 */
interface SheetContextValue {
  dismissible: boolean;
  close: () => void;
}
const SheetContext = React.createContext<SheetContextValue>({
  dismissible: true,
  close: () => {},
});

export interface BottomSheetProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * `false` for a sheet the user has to answer — onboarding, a setup step.
   * Dragging, tapping the backdrop and Escape all stop closing it, and the
   * close button goes, so the sheet's own buttons are the only way out.
   */
  dismissible?: boolean;
  children?: React.ReactNode;
}

/**
 * The phone's panel: rises from the bottom, drags down to close.
 *
 * Use it for anything a phone screen opens over itself — an item's details, a
 * create form, a picker. Open a BottomSheet from inside another and it
 * stacks: the first steps back and the new one rises over it.
 *
 *   <BottomSheet>
 *     <BottomSheetTrigger asChild><Button>New call</Button></BottomSheetTrigger>
 *     <BottomSheetContent title="Create a call" size="full" footer={…}>
 *       …
 *     </BottomSheetContent>
 *   </BottomSheet>
 *
 * On a desktop reach for Sheet or Dialog instead; Dialog already becomes a
 * bottom sheet on a phone, without the drag.
 */
export function BottomSheet({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  dismissible = true,
  children,
}: BottomSheetProps) {
  const nested = React.useContext(NestedContext);
  const Root = nested ? Drawer.NestedRoot : Drawer.Root;

  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : uncontrolledOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!controlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange]
  );

  const context = React.useMemo(
    () => ({ dismissible, close: () => setOpen(false) }),
    [dismissible, setOpen]
  );

  /*
   * The panel portals into the body of the document the sheet is in, not
   * always the page's own. Radix's default is the global document, so a sheet
   * rendered into an iframe — the library's phone frame is one — rose over
   * the page outside the phone instead of inside it. The sheet itself renders
   * no element to ask, hence the hidden marker.
   */
  const [container, setContainer] = React.useState<HTMLElement>();
  const markOwner = React.useCallback((node: HTMLSpanElement | null) => {
    const doc = node?.ownerDocument;
    if (doc && doc !== document) setContainer(doc.body);
  }, []);

  return (
    <SheetContext.Provider value={context}>
      <Root
        open={open}
        onOpenChange={setOpen}
        dismissible={dismissible}
        container={container}
      >
        <span ref={markOwner} hidden />
        {children}
      </Root>
    </SheetContext.Provider>
  );
}

export const BottomSheetTrigger = Drawer.Trigger;

/**
 * Closes the sheet it is in — including one that is not dismissible, which
 * is how the buttons on an onboarding sheet let the user out.
 */
export interface BottomSheetCloseProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Close through the child — a library Button — instead of a bare button. */
  asChild?: boolean;
}

export const BottomSheetClose = React.forwardRef<
  HTMLButtonElement,
  BottomSheetCloseProps
>(function BottomSheetClose({ asChild, onClick, ...props }, ref) {
  const { close } = React.useContext(SheetContext);
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      {...(asChild ? {} : { type: 'button' as const })}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (!event.defaultPrevented) close();
      }}
      {...props}
    />
  );
});

export interface BottomSheetContentProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Drawer.Content>, 'title'>,
    BottomSheetRecipeProps {
  /** Heading, and the sheet's accessible name. */
  title: React.ReactNode;
  /** The kind of thing the sheet is about, above its title: "Impound". */
  eyebrow?: React.ReactNode;
  description?: React.ReactNode;
  /**
   * The confirm for a creation surface — usually an icon button with a tick.
   * Supplying it switches the header to the iOS arrangement: close on the
   * left, title centred, this on the right.
   */
  action?: React.ReactNode;
  /** Buttons along the bottom edge, laid out at equal width. */
  footer?: React.ReactNode;
  /** Defaults to hidden on a sheet that is not dismissible. */
  hideCloseButton?: boolean;
}

export const BottomSheetContent = React.forwardRef<
  React.ElementRef<typeof Drawer.Content>,
  BottomSheetContentProps
>(function BottomSheetContent(
  {
    className,
    size,
    title,
    eyebrow,
    description,
    action,
    footer,
    hideCloseButton,
    children,
    ...props
  },
  ref
) {
  const { dismissible } = React.useContext(SheetContext);
  const showClose = !(hideCloseButton ?? !dismissible);

  const centered = action !== undefined;

  const close = showClose ? (
    <BottomSheetClose asChild>
      {centered ? (
        <Button variant="subtle" size="icon" pill aria-label="Close">
          <X />
        </Button>
      ) : (
        <Button variant="subtle" size="icon-sm" aria-label="Close">
          <X />
        </Button>
      )}
    </BottomSheetClose>
  ) : null;

  return (
    <Drawer.Portal>
      <Drawer.Overlay className={bottomSheetBackdropRecipe()} />
      <Drawer.Content
        ref={ref}
        {...(description ? {} : { 'aria-describedby': undefined })}
        className={cn(bottomSheetRecipe({ size }), className)}
        {...props}
      >
        <NestedContext.Provider value>
          <div
            className={bottomSheetHeaderRecipe({
              layout: centered ? 'centered' : 'standard',
            })}
          >
            {centered ? (
              <>
                {/* Holds the column when there is no close button, so the
                    title stays centred on the panel. */}
                {close ?? <span aria-hidden="true" />}
                <Drawer.Title asChild>
                  <Text variant="body-strong" truncate>
                    {title}
                  </Text>
                </Drawer.Title>
                {action}
              </>
            ) : (
              <>
                <div className="relay-ui-internal min-w-0 space-y-1">
                  {eyebrow ? (
                    <Text variant="body" tone="muted">
                      {eyebrow}
                    </Text>
                  ) : null}
                  <Drawer.Title asChild>
                    <Text variant="h2">{title}</Text>
                  </Drawer.Title>
                  {description ? (
                    <Drawer.Description asChild>
                      <Text variant="body" tone="muted">
                        {description}
                      </Text>
                    </Drawer.Description>
                  ) : null}
                </div>
                {close}
              </>
            )}
          </div>

          {centered && description ? (
            <Drawer.Description className="sr-only">
              {description}
            </Drawer.Description>
          ) : null}

          <div className={bottomSheetBodyRecipe()}>{children}</div>

          {footer ? (
            <div className={bottomSheetFooterRecipe()}>{footer}</div>
          ) : null}
        </NestedContext.Provider>
      </Drawer.Content>
    </Drawer.Portal>
  );
});
